import actions from './actions';
import { createReducer } from '../../../../../../helpers/reducer';
import { keyBy } from '../../../../../../helpers/core';
import { withTZ, withUTC } from '../../../../../../helpers/dateTime';
import { t } from '../../../../../../i18n';

const initState = {
  editForm: null,
  workersToEdit: null,
  isLoading: false,
  totalCount: null,
  filteredCount: null,
  sortableColumns: [],
};

function validate(attribute, value, valueToCompare) {
  let error = '';

  if (attribute === 'started_at') {
    if (valueToCompare && value && withTZ(valueToCompare).isSameOrBefore(withTZ(value))) {
      error = t('dash.validation.start_time_error');
    }
  }

  if (attribute === 'finished_at') {
    if (valueToCompare && value && withTZ(valueToCompare).isSameOrAfter(withTZ(value))) {
      error = t('dash.validation.finish_time_error');
    }
  }

  return error;
}

function onValidateProperty(state, { payload }) {
  const { index, attribute } = payload;
  const { editForm } = state;

  editForm[index][attribute] = {
    ...editForm[index][attribute],
    error: validate(attribute, editForm[index][attribute] && editForm[index][attribute].value),
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function resetTouched(editForm) {
  if (editForm) {
    Object.keys(editForm).forEach(row => {
      Object.keys(editForm[row]).forEach(prop => {
        editForm[row][prop].touched = false;
      });
    });
  }

  return editForm;
}

function formatObject(payload, prop, state, bookingStartedAt, bookingFinishedAt) {
  const propsArray = Object.keys(payload[prop]);

  return keyBy(
    propsArray,
    name => name,
    // eslint-disable-next-line no-use-before-define
    name => formatProp(payload[prop], name, state, bookingStartedAt, bookingFinishedAt),
  );
}

function formatArray(payload, prop, state, bookingStartedAt, bookingFinishedAt) {
  return payload[prop].map((item, index) =>
    // eslint-disable-next-line no-use-before-define
    formatProp(payload[prop], index, state, bookingStartedAt, bookingFinishedAt),
  );
}

function formatProp(payload, prop, state, bookingStartedAt, bookingFinishedAt) {
  const getValue = () => {
    // Use booking start/end time for cases when start or end time is empty
    if (!payload[prop]) {
      if (prop === 'started_at' && bookingStartedAt) {
        return bookingStartedAt;
      }

      if (prop === 'finished_at' && bookingFinishedAt) {
        return bookingFinishedAt;
      }
    }

    // Set original value from bulk check in/out endpoint
    return payload[prop];
  };

  const getTouched = () => {
    /* if (!payload[prop]) {
      // Set touched for star/end time that has been auto filled based on booking data
      return (prop === 'started_at' && !!bookingStartedAt) || (prop === 'finished_at' && !!bookingFinishedAt);
    } */

    return false;
  };

  const getProp = () => ({
    name: prop,
    value: getValue(),
    error: '',
    touched: getTouched(),
  });

  if (Array.isArray(payload[prop])) {
    return formatArray(payload, prop, state, bookingStartedAt, bookingFinishedAt);
  }

  if (typeof payload[prop] === 'object' && payload[prop] != null) {
    return formatObject(payload, prop, state, bookingStartedAt, bookingFinishedAt);
  }

  return getProp();
}

function onLoadClockInClockOutRequest(state) {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadClockInClockOutSuccess(state, { payload }) {
  const {
    data: workersToEdit,
    totalCount,
    filteredCount,
    sortableColumns,
    bookingStartedAt,
    bookingFinishedAt,
  } = payload;

  const keysArray = Object.keys(workersToEdit);
  const editForm = keyBy(
    keysArray,
    prop => prop,
    prop => formatProp(workersToEdit, prop, state, bookingStartedAt, bookingFinishedAt),
  );

  return {
    ...state,
    isLoading: false,
    workersToEdit,
    editForm,
    totalCount,
    filteredCount,
    sortableColumns,
  };
}

function onChangeProperty(state, { payload }) {
  const { index, attribute, value } = payload;
  const { editForm } = state;
  let valueToCompare = null;

  if (attribute === 'finished_at') {
    valueToCompare = editForm[index].started_at.value ? withUTC(editForm[index].started_at.value) : null;
  }

  if (attribute === 'started_at') {
    valueToCompare = editForm[index].finished_at.value ? withUTC(editForm[index].finished_at.value) : null;
  }

  editForm[index][attribute] = {
    value,
    error: validate(attribute, value ? withUTC(value) : null, valueToCompare),
    name: attribute,
    touched: true,
  };

  return { ...state, editForm: { ...editForm } };
}

function onUpdateClockInClockOutRequest(state) {
  return {
    ...state,
    isLoading: true,
  };
}

function onUpdateClockInClockOutSuccess(state) {
  return {
    ...state,
    isLoading: false,
    editForm: resetTouched(state.editForm),
  };
}

function onLoadClockInClockOutCancel(state) {
  return {
    ...state,
    isLoading: false,
  };
}

export default createReducer(initState, {
  [actions.LOAD_CLOCKIN_CLOCKOUT_REQUEST]: onLoadClockInClockOutRequest,
  [actions.LOAD_CLOCKIN_CLOCKOUT_SUCCESS]: onLoadClockInClockOutSuccess,
  [actions.LOAD_CLOCKIN_CLOCKOUT_CANCEL]: onLoadClockInClockOutCancel,
  [actions.UPDATE_CLOCKIN_CLOCKOUT_REQUEST]: onUpdateClockInClockOutRequest,
  [actions.UPDATE_CLOCKIN_CLOCKOUT_SUCCESS]: onUpdateClockInClockOutSuccess,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.VALIDATE_PROPERTY]: onValidateProperty,
});
